import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { formatCurrency, formatDate } from '../lib/utils';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  Plus,
  FilePdf,
  CurrencyDollar,
  Calendar,
  Trash,
  PencilSimple,
  Eye,
  Warning,
  ShieldCheck,
  ClipboardText,
  Check,
  X,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export default function Nomina() {
  const navigate = useNavigate();
  const [empleados, setEmpleados] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('empleados');
  
  // Paste mode (entrada rápida de Excel)
  const [showPasteMode, setShowPasteMode] = useState(false);
  const [pasteData, setPasteData] = useState('');
  const [parsedEmpleados, setParsedEmpleados] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Modal eliminar empleado
  const [showDeleteEmpleadoModal, setShowDeleteEmpleadoModal] = useState(false);
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null);
  const [deleteEmpleadoCode, setDeleteEmpleadoCode] = useState('');
  const [deletingEmpleado, setDeletingEmpleado] = useState(false);
  
  // Modal editar empleado (con super usuario)
  const [showEditEmpleadoModal, setShowEditEmpleadoModal] = useState(false);
  const [empleadoToEdit, setEmpleadoToEdit] = useState(null);
  const [editEmpleadoCode, setEditEmpleadoCode] = useState('');
  const [validatingEditEmpleado, setValidatingEditEmpleado] = useState(false);
  
  // Modal eliminar pago
  const [showDeletePagoModal, setShowDeletePagoModal] = useState(false);
  const [pagoToDelete, setPagoToDelete] = useState(null);
  const [deletePagoCode, setDeletePagoCode] = useState('');
  const [deletingPago, setDeletingPago] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [empRes, pagosRes] = await Promise.all([
        axios.get(`${API_BASE}/empleados`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/nomina/pagos`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEmpleados(empRes.data);
      setPagos(pagosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // ============== FUNCIONES DE ENTRADA RÁPIDA EXCEL ==============
  
  const handlePasteData = () => {
    if (!pasteData.trim()) {
      toast.error('No hay datos para procesar');
      return;
    }

    try {
      // Limpiar los datos: reemplazar saltos de línea dentro de celdas
      let cleanedData = pasteData;
      cleanedData = cleanedData.replace(/"([^"]*)"/g, (match, p1) => {
        return p1.replace(/\n/g, ' ').replace(/\r/g, '');
      });
      
      const lines = cleanedData.trim().split('\n');
      const parsed = [];

      // Verificar si la primera línea son encabezados o datos
      const firstLine = lines[0].split('\t').map(h => h.trim());
      const firstCellUpper = firstLine[0].toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Si la primera celda contiene "NOMBRE" o "APELLIDO", son encabezados
      // Si no, asumimos que son datos y usamos el orden predeterminado del Excel de Elanco
      const hasHeaders = firstCellUpper.includes('NOMBRE') || firstCellUpper.includes('APELLIDO') || 
                         firstCellUpper.includes('EMPLEADO') || firstCellUpper === 'TRABAJADOR';
      
      let colMap = {};
      let startRow = 0;
      
      if (hasHeaders) {
        // Usar encabezados para mapear columnas
        const headers = firstLine;
        // Debug removed
        
        headers.forEach((header, idx) => {
          const h = header.toUpperCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, ' ')
            .trim();
          
          if (h.includes('NOMBRE') || h.includes('APELLIDO')) {
            if (colMap.nombre === undefined) colMap.nombre = idx;
          }
          else if (h === 'APODO' || h.includes('SOBRENOMBRE')) colMap.apodo = idx;
          else if (h.includes('CEDULA')) colMap.cedula = idx;
          else if (h.includes('LOTE')) colMap.lote_numero = idx;
          else if (h === 'PROYECTO' || h === 'AREA') colMap.proyecto = idx;
          else if (h === 'PUESTO' || h === 'CARGO') colMap.puesto = idx;
          else if (h.includes('SUELDO') || h.includes('SALARIO')) {
            if (colMap.sueldo_mensual === undefined) colMap.sueldo_mensual = idx;
          }
          else if (h === 'AFP') colMap.afp = idx;
          else if (h === 'ARS') colMap.ars = idx;
          else if (h === 'CXC') colMap.cxc = idx;
          else if (h.includes('SEG') || h.includes('MEDICO')) {
            if (colMap.seg_medico === undefined) colMap.seg_medico = idx;
          }
        });
        startRow = 1; // Saltar la fila de encabezados
      } else {
        // No hay encabezados - usar orden predeterminado del Excel de Elanco:
        // NOMBRE | Apodo | CEDULA | LOTE# | PROYECTO | PUESTO | SUELDO MENSUAL | AFP | ARS | CXC | Seg. Medico
        // Debug removed
        colMap = {
          nombre: 0,
          apodo: 1,
          cedula: 2,
          lote_numero: 3,
          proyecto: 4,
          puesto: 5,
          sueldo_mensual: 6,
          afp: 7,
          ars: 8,
          cxc: 9,
          seg_medico: 10
        };
        startRow = 0; // Procesar desde la primera fila
        toast.info('Sin encabezados detectados. Usando orden: Nombre, Apodo, Cédula, Lote#, Proyecto, Puesto, Sueldo...');
      }

      // Debug removed

      // Procesar filas de datos
      for (let i = startRow; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const cols = line.split('\t');
        
        const nombre = cols[colMap.nombre]?.trim();
        if (!nombre) continue;

        const puesto = cols[colMap.puesto]?.trim() || 'OTRO';
        
        // Parsear sueldo (quitar $, RD$, comas)
        const sueldoRaw = cols[colMap.sueldo_mensual]?.trim() || '0';
        const sueldo = parseFloat(sueldoRaw.replace(/[$,RD\s]/gi, '')) || 0;
        if (sueldo <= 0) continue;

        // Parsear deducciones
        const parseAmount = (idx) => {
          if (idx === undefined) return 0;
          const raw = cols[idx]?.trim() || '0';
          return parseFloat(raw.replace(/[$,RD\s]/gi, '')) || 0;
        };

        parsed.push({
          id: Date.now() + Math.random(),
          nombre_completo: nombre,
          apodo: cols[colMap.apodo]?.trim() || '',
          cedula: cols[colMap.cedula]?.trim() || '',
          lote_numero: cols[colMap.lote_numero]?.trim() || '',
          proyecto: cols[colMap.proyecto]?.trim() || 'GENERAL',
          puesto: puesto,
          sueldo_mensual: sueldo,
          afp_fijo: parseAmount(colMap.afp),
          ars_fijo: parseAmount(colMap.ars),
          cxc_fijo: parseAmount(colMap.cxc),
          seg_medico_fijo: parseAmount(colMap.seg_medico),
        });
      }

      if (parsed.length > 0) {
        setParsedEmpleados(parsed);
        setPasteData('');
        toast.success(`${parsed.length} empleados parseados. Revise y confirme.`);
      } else {
        toast.error('No se encontraron datos válidos');
      }
    } catch (error) {
      // Error silenciado
      toast.error('Error al procesar los datos');
    }
  };

  const updateParsedEmpleado = (index, field, value) => {
    const updated = [...parsedEmpleados];
    updated[index][field] = field.includes('fijo') || field === 'sueldo_mensual' 
      ? parseFloat(value) || 0 
      : value;
    setParsedEmpleados(updated);
  };

  const removeParsedEmpleado = (index) => {
    setParsedEmpleados(parsedEmpleados.filter((_, i) => i !== index));
  };

  const handleImportEmpleados = async () => {
    if (parsedEmpleados.length === 0) {
      toast.error('No hay empleados para importar');
      return;
    }

    setImporting(true);
    const token = localStorage.getItem('token');

    try {
      const payload = {
        empleados: parsedEmpleados.map(emp => ({
          nombre_completo: emp.nombre_completo,
          apodo: emp.apodo || null,
          cedula: emp.cedula || null,
          lote_numero: emp.lote_numero || null,
          proyecto: emp.proyecto || 'GENERAL',
          puesto: emp.puesto,
          sueldo_mensual: emp.sueldo_mensual,
          afp_fijo: emp.afp_fijo || 0,
          ars_fijo: emp.ars_fijo || 0,
          cxc_fijo: emp.cxc_fijo || 0,
          seg_medico_fijo: emp.seg_medico_fijo || 0,
        }))
      };

      const response = await axios.post(`${API_BASE}/empleados/importar`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.mensaje);
      setParsedEmpleados([]);
      setShowPasteMode(false);
      loadData(); // Recargar lista de empleados
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setParsedEmpleados([]);
    setPasteData('');
    setShowPasteMode(false);
  };

  // ============== FIN FUNCIONES ENTRADA RÁPIDA ==============

  const openDeleteEmpleadoModal = (empleado) => {
    setEmpleadoToDelete(empleado);
    setDeleteEmpleadoCode('');
    setShowDeleteEmpleadoModal(true);
  };

  const openEditEmpleadoModal = (empleado) => {
    setEmpleadoToEdit(empleado);
    setEditEmpleadoCode('');
    setShowEditEmpleadoModal(true);
  };

  const handleValidateEditEmpleado = async () => {
    if (!editEmpleadoCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    setValidatingEditEmpleado(true);
    try {
      await authAPI.validateSuperuser(editEmpleadoCode);
      setShowEditEmpleadoModal(false);
      // Navegar al formulario de edición
      window.location.href = `/nomina/empleado/${empleadoToEdit.id}`;
    } catch (error) {
      toast.error('Código de super usuario inválido');
    } finally {
      setValidatingEditEmpleado(false);
    }
  };

  const handleDeleteEmpleado = async () => {
    if (!deleteEmpleadoCode || !empleadoToDelete) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setDeletingEmpleado(true);
    const token = localStorage.getItem('token');
    try {
      await authAPI.validateSuperuser(deleteEmpleadoCode);
      await axios.delete(`${API_BASE}/empleados/${empleadoToDelete.id}`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { superuser_code: deleteEmpleadoCode }
      });
      toast.success('Empleado eliminado');
      setShowDeleteEmpleadoModal(false);
      loadData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error('Error al eliminar');
      }
    } finally {
      setDeletingEmpleado(false);
    }
  };

  const openDeletePagoModal = (pago) => {
    setPagoToDelete(pago);
    setDeletePagoCode('');
    setShowDeletePagoModal(true);
  };

  const handleDeletePago = async () => {
    if (!deletePagoCode || !pagoToDelete) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setDeletingPago(true);
    const token = localStorage.getItem('token');
    try {
      await authAPI.validateSuperuser(deletePagoCode);
      await axios.delete(`${API_BASE}/nomina/pagos/${pagoToDelete.id}`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { superuser_code: deletePagoCode }
      });
      toast.success('Pago eliminado');
      setShowDeletePagoModal(false);
      loadData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error('Error al eliminar');
      }
    } finally {
      setDeletingPago(false);
    }
  };

  const handleDownloadNomina = async (pagoId) => {
    try {
      toast.loading('Generando PDF...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reports/nomina/${pagoId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nomina_${pagoId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.dismiss();
      toast.success('PDF descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar PDF');
    }
  };

  const handleDownloadVolantes = async (pagoId) => {
    try {
      toast.loading('Generando volantes...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reports/nomina/${pagoId}/volantes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `volantes_${pagoId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.dismiss();
      toast.success('Volantes descargados');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar volantes');
    }
  };

  const totalSueldosMensual = empleados.filter(e => e.activo).reduce((sum, e) => sum + e.sueldo_mensual, 0);
  const empleadosActivos = empleados.filter(e => e.activo).length;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="nomina-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Nómina
            </h1>
            <p className="text-muted-foreground">
              Gestión de empleados y pagos de nómina
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={showPasteMode ? "default" : "outline"} 
              onClick={() => setShowPasteMode(!showPasteMode)}
              className={showPasteMode ? "bg-[#F69031] hover:bg-[#E5801F]" : ""}
              data-testid="paste-excel-btn"
            >
              <ClipboardText size={18} className="mr-2" />
              {showPasteMode ? 'Ocultar Pegado' : 'Pegar desde Excel'}
            </Button>
            <Link to="/nomina/empleado/nuevo">
              <Button variant="outline" data-testid="new-empleado-btn">
                <Plus size={18} className="mr-2" />
                Nuevo Empleado
              </Button>
            </Link>
            <Link to="/nomina/pago/nuevo">
              <Button className="bg-[#E73425] hover:bg-[#D81C23]" data-testid="new-pago-btn">
                <CurrencyDollar size={18} className="mr-2" />
                Generar Pago
              </Button>
            </Link>
          </div>
        </div>

        {/* Paste Area - Entrada Rápida Excel */}
        {showPasteMode && (
          <Card className="border-border/60 border-dashed border-2 border-[#F69031]/50 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-semibold">Pegue los datos desde Excel</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Seleccione y copie las celdas de Excel incluyendo la fila de encabezados. El sistema reconoce automáticamente columnas con estos nombres:
                  </p>
                  <div className="bg-white/70 p-2 rounded mt-2 text-xs space-y-1">
                    <p><strong>Obligatorias:</strong> NOMBRE(S) Y APELLIDO(S), PUESTO (o CARGO), SUELDO MENSUAL (o SALARIO)</p>
                    <p><strong>Opcionales:</strong> Apodo, CEDULA, LOTE #, PROYECTO, AFP, ARS, CXC, Seg. Medico</p>
                  </div>
                </div>
                <Textarea
                  placeholder="Pegue aquí los datos copiados de Excel (Ctrl+V)..."
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  rows={5}
                  className="font-mono text-sm bg-white/80"
                  data-testid="paste-area"
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={handlePasteData}
                    className="bg-[#F69031] hover:bg-[#E5801F]"
                    disabled={!pasteData.trim()}
                  >
                    <Check size={18} className="mr-2" />
                    Procesar Datos
                  </Button>
                  <Button variant="outline" onClick={() => { setPasteData(''); setShowPasteMode(false); }}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview de Empleados Parseados */}
        {parsedEmpleados.length > 0 && (
          <Card className="border-border/60 border-2 border-emerald-300 bg-emerald-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <Check size={24} />
                  Vista Previa ({parsedEmpleados.length} empleados)
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={cancelImport}>
                    <X size={16} className="mr-1" /> Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleImportEmpleados}
                    disabled={importing}
                  >
                    {importing ? (
                      <div className="spinner h-4 w-4 mr-2" />
                    ) : (
                      <Check size={16} className="mr-1" />
                    )}
                    Importar {parsedEmpleados.length}
                  </Button>
                </div>
              </div>
              <CardDescription>
                Revise los datos antes de importar. Puede editar o eliminar filas.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-emerald-100/50">
                      <th className="p-2 text-left font-semibold">Nombre</th>
                      <th className="p-2 text-left font-semibold w-24">Apodo</th>
                      <th className="p-2 text-center font-semibold w-16">Lote #</th>
                      <th className="p-2 text-left font-semibold w-32">Puesto</th>
                      <th className="p-2 text-left font-semibold w-24">Proyecto</th>
                      <th className="p-2 text-right font-semibold w-28">Sueldo</th>
                      <th className="p-2 text-right font-semibold w-20 bg-red-50">CXC</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedEmpleados.map((emp, idx) => (
                      <tr key={emp.id} className="border-b hover:bg-emerald-50/50">
                        <td className="p-1">
                          <Input
                            value={emp.nombre_completo}
                            onChange={(e) => updateParsedEmpleado(idx, 'nombre_completo', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={emp.apodo}
                            onChange={(e) => updateParsedEmpleado(idx, 'apodo', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={emp.lote_numero}
                            onChange={(e) => updateParsedEmpleado(idx, 'lote_numero', e.target.value)}
                            className="h-8 text-sm text-center"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={emp.puesto}
                            onChange={(e) => updateParsedEmpleado(idx, 'puesto', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={emp.proyecto}
                            onChange={(e) => updateParsedEmpleado(idx, 'proyecto', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={emp.sueldo_mensual}
                            onChange={(e) => updateParsedEmpleado(idx, 'sueldo_mensual', e.target.value)}
                            className="h-8 text-sm text-right"
                          />
                        </td>
                        <td className="p-1 bg-red-50">
                          <Input
                            type="number"
                            value={emp.cxc_fijo}
                            onChange={(e) => updateParsedEmpleado(idx, 'cxc_fijo', e.target.value)}
                            className="h-8 text-sm text-right bg-red-50"
                          />
                        </td>
                        <td className="p-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                            onClick={() => removeParsedEmpleado(idx)}
                          >
                            <Trash size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Empleados Activos</p>
              <p className="metric-value text-[#E73425]">{empleadosActivos}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Nómina Mensual</p>
              <p className="metric-value text-[#F69031]">{formatCurrency(totalSueldosMensual)}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Pagos Registrados</p>
              <p className="metric-value text-slate-600">{pagos.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="empleados" data-testid="tab-empleados">
              <Users size={16} className="mr-2" />
              Empleados ({empleados.length})
            </TabsTrigger>
            <TabsTrigger value="pagos" data-testid="tab-pagos">
              <Calendar size={16} className="mr-2" />
              Historial de Pagos ({pagos.length})
            </TabsTrigger>
          </TabsList>

          {/* Empleados Tab */}
          <TabsContent value="empleados" className="space-y-4">
            {empleados.length > 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b table-header">
                          <th className="text-left p-4">Nombre</th>
                          <th className="text-left p-4">Apodo</th>
                          <th className="text-center p-4">Lote #</th>
                          <th className="text-left p-4">Puesto</th>
                          <th className="text-left p-4">Proyecto</th>
                          <th className="text-right p-4">Sueldo Mensual</th>
                          <th className="text-right p-4">Deducciones</th>
                          <th className="text-right p-4">Neto Quinc.</th>
                          <th className="text-center p-4">Estado</th>
                          <th className="text-center p-4">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empleados.map((emp) => {
                          const totalDeducciones = (emp.afp_fijo || 0) + (emp.ars_fijo || 0) + (emp.cxc_fijo || 0) + (emp.seg_medico_fijo || 0);
                          const netoQuincenal = emp.sueldo_quincenal - (totalDeducciones / 2);
                          return (
                            <tr key={emp.id} className="border-b table-row" data-testid={`empleado-${emp.id}`}>
                              <td className="p-4">
                                <div>
                                  <p className="font-medium">{emp.nombre_completo}</p>
                                  {emp.cedula && (
                                    <p className="text-xs text-muted-foreground font-mono">{emp.cedula}</p>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 text-muted-foreground">{emp.apodo || '-'}</td>
                              <td className="p-4 text-center">
                                <Badge variant="secondary">{emp.lote_numero || 'ALL'}</Badge>
                              </td>
                              <td className="p-4">
                                <Badge variant="outline">{emp.puesto}</Badge>
                              </td>
                              <td className="p-4 text-sm">{emp.proyecto || 'GENERAL'}</td>
                              <td className="p-4 text-right font-bold font-mono">
                                {formatCurrency(emp.sueldo_mensual)}
                              </td>
                              <td className="p-4 text-right">
                                {totalDeducciones > 0 ? (
                                  <span className="text-red-600 font-mono text-xs" title={`AFP: ${emp.afp_fijo || 0} | ARS: ${emp.ars_fijo || 0} | CXC: ${emp.cxc_fijo || 0} | Seg: ${emp.seg_medico_fijo || 0}`}>
                                    -{formatCurrency(totalDeducciones)}/mes
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </td>
                              <td className="p-4 text-right font-mono text-emerald-600">
                                {formatCurrency(netoQuincenal)}
                              </td>
                              <td className="p-4 text-center">
                                <Badge variant={emp.activo ? 'default' : 'secondary'}>
                                  {emp.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <div className="flex justify-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 text-blue-600 hover:text-blue-700"
                                    onClick={() => openEditEmpleadoModal(emp)}
                                    data-testid={`edit-empleado-${emp.id}`}
                                  >
                                    <PencilSimple size={16} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-700"
                                    onClick={() => openDeleteEmpleadoModal(emp)}
                                    data-testid={`delete-empleado-${emp.id}`}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border/60">
                <CardContent className="py-12">
                  <div className="empty-state">
                    <Users size={64} className="text-muted-foreground/50 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No hay empleados registrados</h3>
                    <p className="text-muted-foreground mb-6">
                      Comience agregando su primer empleado
                    </p>
                    <Link to="/nomina/empleado/nuevo">
                      <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                        <Plus size={18} className="mr-2" />
                        Agregar Empleado
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Pagos Tab */}
          <TabsContent value="pagos" className="space-y-4">
            {pagos.length > 0 ? (
              <div className="space-y-4">
                {pagos.map((pago) => (
                  <Card key={pago.id} className="border-border/60" data-testid={`pago-${pago.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <CurrencyDollar size={24} className="text-[#E73425]" weight="fill" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">
                                Pago {pago.tipo_pago === 'quincenal' ? 'Quincenal' : 'Mensual'}
                              </h3>
                              <Badge variant="outline">
                                {pago.empleados_count} empleados
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Fecha: {formatDate(pago.fecha_pago)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Total a Pagar</p>
                            <p className="text-xl font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatCurrency(pago.total_neto)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadNomina(pago.id)}
                              data-testid={`download-nomina-${pago.id}`}
                            >
                              <FilePdf size={16} className="mr-1" />
                              Nómina
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadVolantes(pago.id)}
                              data-testid={`download-volantes-${pago.id}`}
                            >
                              <FilePdf size={16} className="mr-1" />
                              Volantes
                            </Button>
                            <Link to={`/nomina/pago/${pago.id}`}>
                              <Button variant="outline" size="sm">
                                <Eye size={16} className="mr-1" />
                                Ver
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => openDeletePagoModal(pago)}
                              data-testid={`delete-pago-${pago.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-border/60">
                <CardContent className="py-12">
                  <div className="empty-state">
                    <Calendar size={64} className="text-muted-foreground/50 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No hay pagos registrados</h3>
                    <p className="text-muted-foreground mb-6">
                      Genere su primer pago de nómina
                    </p>
                    <Link to="/nomina/pago/nuevo">
                      <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                        <CurrencyDollar size={18} className="mr-2" />
                        Generar Pago
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Modal Eliminar Empleado */}
        <Dialog open={showDeleteEmpleadoModal} onOpenChange={setShowDeleteEmpleadoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Empleado
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer
              </DialogDescription>
            </DialogHeader>
            
            {empleadoToDelete && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-2">
                    Está a punto de eliminar al empleado:
                  </p>
                  <p className="font-semibold text-red-700">{empleadoToDelete.nombre_completo}</p>
                  <p className="text-sm text-red-600">Cédula: {empleadoToDelete.cedula}</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-empleado-code">Código de Super Usuario</Label>
                  <Input
                    id="delete-empleado-code"
                    type="password"
                    value={deleteEmpleadoCode}
                    onChange={(e) => setDeleteEmpleadoCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="delete-empleado-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteEmpleadoModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteEmpleado}
                disabled={deletingEmpleado || !deleteEmpleadoCode}
              >
                {deletingEmpleado ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Empleado
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal Editar Empleado (con Super Usuario) */}
        <Dialog open={showEditEmpleadoModal} onOpenChange={setShowEditEmpleadoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <PencilSimple size={24} />
                Editar Empleado
              </DialogTitle>
              <DialogDescription>
                Ingrese el código de super usuario para editar
              </DialogDescription>
            </DialogHeader>
            
            {empleadoToEdit && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 mb-2">
                    Editar al empleado:
                  </p>
                  <p className="font-semibold text-blue-700">{empleadoToEdit.nombre_completo}</p>
                  <p className="text-sm text-blue-600">Puesto: {empleadoToEdit.puesto}</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-empleado-code">Código de Super Usuario</Label>
                  <Input
                    id="edit-empleado-code"
                    type="password"
                    value={editEmpleadoCode}
                    onChange={(e) => setEditEmpleadoCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="edit-empleado-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEditEmpleadoModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleValidateEditEmpleado}
                disabled={validatingEditEmpleado || !editEmpleadoCode}
              >
                {validatingEditEmpleado ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <ShieldCheck size={16} className="mr-2" />
                    Validar y Editar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal Eliminar Pago */}
        <Dialog open={showDeletePagoModal} onOpenChange={setShowDeletePagoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Pago de Nómina
              </DialogTitle>
              <DialogDescription>
                Esta acción eliminará también los gastos asociados
              </DialogDescription>
            </DialogHeader>
            
            {pagoToDelete && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-2">
                    Está a punto de eliminar el pago:
                  </p>
                  <p className="font-semibold text-red-700">
                    Pago {pagoToDelete.tipo_pago === 'quincenal' ? 'Quincenal' : 'Mensual'}
                  </p>
                  <p className="text-sm text-red-600">Fecha: {formatDate(pagoToDelete.fecha_pago)}</p>
                  <p className="text-sm text-red-600">Total: {formatCurrency(pagoToDelete.total_neto)}</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-pago-code">Código de Super Usuario</Label>
                  <Input
                    id="delete-pago-code"
                    type="password"
                    value={deletePagoCode}
                    onChange={(e) => setDeletePagoCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="delete-pago-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeletePagoModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeletePago}
                disabled={deletingPago || !deletePagoCode}
              >
                {deletingPago ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
